import {useBrowserDeviceRestore} from './src/native/useBrowserNavigation';
import {configureDisplayTimeZone} from './src/shared/display-timezone.js';
import {PaletteContext} from './src/native/delivered/theme/tokens';
import {CopyContext} from './src/native/delivered/copy';
import {MapPreferencesProvider} from "./src/native/MapPreferences";
import {AdminDashboard} from './src/native/AdminDashboard';
import {PublicDeletion} from './src/native/PublicDeletion';
import {PrivacyNotice} from './src/native/PrivacyNotice';
import { HelpPanel } from "./src/native/HelpPanel";
import { FeatureSupportNote } from "./src/native/FeatureSupportNote";
import { featureSupport } from "./src/shared/feature-support.js";
import {
  PhoneNotificationsProvider,
  PhoneNotificationPanel,
} from "./src/native/PhoneNotifications";
import { DeliveredApplication } from "./src/native/delivered/DeliveredApplication";
import {CustomerFleet,type CustomerFleetViewState} from './src/native/WebFleetPanel';
import {useFleetInvitationLink} from './src/native/useFleetInvitationLink';
import {CustomerDialog} from './src/native/CustomerDialog';
import {Button as CustomerButton} from './src/native/delivered/components/primitives';
import {QtrackerMenu} from './src/native/QtrackerMenu';
import {
  LayoutProvider,
  useLayout,
  useDesignStyles,
} from "./src/native/LayoutContext";
import { AppearanceControls } from "./src/native/AppearanceControls";
import {resolveAppearance} from './src/shared/appearance-model.js';
import type {Appearance} from './src/shared/appearance-types';
import {
  layoutPalette,
  APP_NAVIGATION,
  deliveredDesign,
} from "./src/shared/layout-model.js";
import { DesignIcon, featureIcons } from "./src/native/DesignPrimitives";
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import {
  ActivityIndicator,
  AppState,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StatusBar as NativeStatusBar,
  StyleSheet,
  Switch,
  Text,
  TextInput,
  useColorScheme,
  View,
} from "react-native";
import { GpsZoneModal, PositionSourcesModal } from "./src/native/GpsZoneModal";
import { Info } from "./src/native/ProductControls";
import { StatusBar } from "expo-status-bar";
import {
  SafeAreaProvider,
  SafeAreaView,
  initialWindowMetrics,
} from "react-native-safe-area-context";
import {
  sessionVault,
  readPreference,
  writePreference,
} from "./src/native/secure-vault";
import { LocalApiScreen } from "./src/native/LocalApiScreen";
import { ConnectedTrackerScreen } from "./src/native/ConnectedTrackerScreen";
import { TrackerPasswordModal } from "./src/native/TrackerPasswordModal";
import { AccountPasswordModal } from "./src/native/AccountPasswordModal";
import { ActivityProfileModal } from "./src/native/ActivityProfileModal";
import {
  HardwareFeatureModal,
  hardwareFeatureIds,
} from "./src/native/HardwarePanel";
import MapDiagnostic from "./src/native/MapDiagnostic";
import { LocalTrackerSession } from "./src/native/local-session";
import { pendingAlarms } from "./src/native/tracker-view";
import { translate } from "./src/i18n/catalogues.js";
import {
  featureCatalog,
  featuresForSection,
} from "./src/native/feature-catalog.js";

type Locale = "en-GB" | "da-DK";
type ThemeMode = "light" | "dark";
type ViewKey = "overview" | "map" | "care" | "alarms" | "events" | "activity" | "device";
type EventFilter = "all" | "critical" | "system";
type FeatureStatus = "ready" | "demo" | "gated" | "locked";
type FeatureValue = string | boolean | number;
type FeatureControl = {
  type: "choice" | "toggle" | "field" | "action";
  key: string;
  labelKey: string;
  value?: FeatureValue;
  options?: string[];
  inputMode?: "text" | "numeric" | "email" | "password";
};
type FeatureDefinition = {
  id: string;
  section:
    | "location"
    | "care"
    | "communication"
    | "safety"
    | "device"
    | "account";
  symbol: string;
  status: FeatureStatus;
  titleKey: string;
  summaryKey: string;
  controls: FeatureControl[];
};
type FeatureValues = Record<string, Record<string, FeatureValue>>;

type Palette = {
  canvas: string;
  surface: string;
  surfaceSoft: string;
  ink: string;
  muted: string;
  line: string;
  primary: string;
  primaryText: string;
  primarySoft: string;
  ok: string;
  okSoft: string;
  attention: string;
  attentionSoft: string;
  critical: string;
  criticalSoft: string;
  map: string;
  road: string;
};

const palettes: Record<ThemeMode, Palette> = {
  light: {
    canvas: "#EEF2EF",
    surface: "#FFFFFF",
    surfaceSoft: "#F5F7F6",
    ink: "#17231F",
    muted: "#65716D",
    line: "#E2E9E5",
    primary: "#106B5F",
    primaryText: "#FFFFFF",
    primarySoft: "#DFF2ED",
    ok: "#16785A",
    okSoft: "#E4F5EE",
    attention: "#A85F08",
    attentionSoft: "#FFF0D7",
    critical: "#B42318",
    criticalSoft: "#FEE9E7",
    map: "#DDEAE4",
    road: "rgba(255,255,255,0.92)",
  },
  dark: {
    canvas: "#0B1210",
    surface: "#141D1A",
    surfaceSoft: "#1A2622",
    ink: "#F2F7F5",
    muted: "#A7B5B0",
    line: "#26342F",
    primary: "#68D3C1",
    primaryText: "#09211D",
    primarySoft: "#183A34",
    ok: "#6BD3A8",
    okSoft: "#18372B",
    attention: "#F2BD62",
    attentionSoft: "#3B2C17",
    critical: "#FF8A80",
    criticalSoft: "#43211F",
    map: "#20352E",
    road: "rgba(70,92,83,0.92)",
  },
};

const navItems = APP_NAVIGATION as Array<{ key: ViewKey; symbol: string }>;

const features = featureCatalog as FeatureDefinition[];

const SERVICE_URL =
  (Platform.OS==='web'&&typeof window!=='undefined'?window.location.origin:process.env.EXPO_PUBLIC_QSMART_API_URL) ??
  "https://qtracker.dk";
const TEST_MODE =
  process.env.EXPO_PUBLIC_QSMART_TEST_MODE === "simulator"
    ? "simulator"
    : "hardware-test";
export default function App() {
  if(Platform.OS==='web'&&typeof window!=='undefined'&&/^\/delete-account\/?$/.test(window.location.pathname))return <SafeAreaProvider><LayoutProvider><PublicDeletion baseUrl={window.location.origin}/></LayoutProvider></SafeAreaProvider>;
  if(Platform.OS==='web'&&typeof window!=='undefined'&&/^\/privacy\/?$/.test(window.location.pathname))return <SafeAreaProvider><LayoutProvider><PrivacyNotice/></LayoutProvider></SafeAreaProvider>;
  if(Platform.OS==='web'&&typeof window!=='undefined'&&/^\/admin\/?$/.test(window.location.pathname))return <SafeAreaProvider><LayoutProvider><AdminDashboard baseUrl={window.location.origin}/></LayoutProvider></SafeAreaProvider>;
  return (
    <SafeAreaProvider initialMetrics={initialWindowMetrics}>
      <LayoutProvider>
        <MapPreferencesProvider><AppContent /></MapPreferencesProvider>
      </LayoutProvider>
    </SafeAreaProvider>
  );
}
function AppContent() {
  const invitationLink=useFleetInvitationLink();
  const {layout,appearance,selectAppearance}=useLayout(),designStyles=useDesignStyles();
  const {theme:themeMode,language:locale}=appearance;
  configureDisplayTimeZone(appearance.timeZone??'auto');
  const [view, setView] = useState<ViewKey>("overview");
  const fleetViews=useRef(new Map<string,CustomerFleetViewState>());
  const [fleetMenuRequest,setFleetMenuRequest]=useState(0);
  const openFleet=()=>setFleetMenuRequest(value=>value+1);
  const fleetListGeneration=useRef(0);
  const [activeFleetId,setActiveFleetId]=useState<string|null>(null),[returnFleetId,setReturnFleetId]=useState<string|null>(null),[assignedFleets,setAssignedFleets]=useState<import('./src/native/customer-types').AssignedFleet[]>([]);
  const [session] = useState(
    () => new LocalTrackerSession(SERVICE_URL, undefined, 15000, sessionVault),
  );
  const sessionState = useSyncExternalStore(
    session.subscribe,
    session.getSnapshot,
    session.getSnapshot,
  );

  const browserNavigationReady=useBrowserDeviceRestore(session,sessionState);  useEffect(()=>{let active=true;fleetViews.current.clear();setFleetMenuRequest(0);fleetListGeneration.current++;setAssignedFleets([]);setActiveFleetId(null);setReturnFleetId(null);if(!sessionState.signedIn||Platform.OS!=='web')return()=>{active=false;};let pending=false;const refresh=async()=>{if(pending)return;pending=true;const generation=fleetListGeneration.current;try{const v=await session.assignedFleets();if(active&&generation===fleetListGeneration.current){setAssignedFleets(v.fleets);for(const key of fleetViews.current.keys())if(!v.fleets.some(f=>f.id===key))fleetViews.current.delete(key);setReturnFleetId(old=>old&&!v.fleets.some(f=>f.id===old)?null:old);setActiveFleetId(old=>old&&!v.fleets.some(f=>f.id===old)?null:old);}}catch{}finally{pending=false;}};void refresh();const timer=setInterval(()=>void refresh(),15000);return()=>{active=false;clearInterval(timer);};},[sessionState.signedIn,sessionState.accountEmail,session]);
  const [booting, setBooting] = useState(true),
    [toolsOpen, setToolsOpen] = useState(false),
    [mapDiagnostic, setMapDiagnostic] = useState(false),
    [customizing, setCustomizing] = useState(false),
    [activeFeatureId, setActiveFeatureId] = useState<string | null>(null),
    [activityRouteDate, setActivityRouteDate] = useState<string | undefined>();
  const palette = useMemo(
    () => layoutPalette(palettes[themeMode], themeMode==='dark'?'modern2':'accessible', themeMode === "dark"),
    [themeMode],
  );
  const styles = useMemo(
    () => createStyles(palette, designStyles),
    [palette, designStyles],
  );
  const t = (key: string, parameters: Record<string, string | number> = {}) =>
    translate(locale, key, parameters);
  useEffect(() => {
    let active = true;
    void (async () => {
      const [language, savedLayout, savedAppearance] = await Promise.all([
        readPreference("language"),
        readPreference("layout"),
        readPreference('appearance'),
      ]);
      if (!active) return;
      let stored;
      try {stored=savedAppearance?JSON.parse(savedAppearance):savedLayout?{layout:savedLayout}:appearance;}catch {stored=savedLayout?{layout:savedLayout}:appearance;}
      selectAppearance(resolveAppearance(stored,language==='en-GB'?'en-GB':language==='da-DK'?'da-DK':locale) as Appearance);
      await session.restore({address:SERVICE_URL,mode:TEST_MODE});
      if (active) setBooting(false);
    })();
    return () => {
      active = false;
      session.dispose();
    };
  }, [session]);
  useEffect(() => {
    if (!booting) {
      void writePreference('appearance',JSON.stringify(appearance));
      void writePreference('language',locale);
    }
  }, [appearance, booting]);
  useEffect(() => {
    if (sessionState.appearancePreference)
      selectAppearance(resolveAppearance(sessionState.appearancePreference,locale) as Appearance);
  }, [sessionState.appearancePreference,sessionState.accountEmail,selectAppearance]);
  useEffect(() => {
    session.setForeground(
      !mapDiagnostic && (AppState.currentState ?? "active") === "active",
    );
    const sub = AppState.addEventListener("change", (state) =>
      session.setForeground(!mapDiagnostic && state === "active"),
    );
    return () => sub.remove();
  }, [session, mapDiagnostic]);
  const chooseAppearance=(patch:Partial<Appearance>)=>{
    selectAppearance(patch);
    if(sessionState.signedIn)void session.saveAppearance(patch);
  };
  const onLanguage = () => chooseAppearance({language:locale === "da-DK" ? "en-GB" : "da-DK"}),
    onTheme = () => chooseAppearance({theme:themeMode === "dark" ? "light" : "dark"});
  const leaveServer = () => {
    setActiveFeatureId(null);
    setView("overview");
    void session.logout();
  };
  const openFeature = (id: string) => setActiveFeatureId(id),
    activeFeature = features.find((f) => f.id === activeFeatureId);
  const toolbar = (
    <View
      style={{
        flexDirection: "row",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "space-between",
        paddingHorizontal: 16,
        paddingVertical: 12,
        gap: 12,
        backgroundColor: palette.canvas,
      }}
    >
      {sessionState.signedIn?<QtrackerMenu state={sessionState} session={session} palette={palette} locale={locale} invitationCode={invitationLink.code??undefined} onInvitationHandled={invitationLink.handled} assigned={assignedFleets} openDevicesRequest={fleetMenuRequest} onDevicesOpened={()=>setFleetMenuRequest(0)} fleetName={activeFleetId?assignedFleets.find(f=>f.id===activeFleetId)?.name:undefined} onFleet={(id,created)=>{if(created){fleetListGeneration.current++;setAssignedFleets(old=>[...old.filter(f=>f.id!==id),created]);}setActiveFleetId(id);setReturnFleetId(null);}} onSelected={()=>{setActiveFleetId(null);setReturnFleetId(null);setView('overview');setActiveFeatureId(null);}}/>:<Text
        style={{
          color: palette.ink,
          fontSize: 14,
          fontWeight: "700",
          flexShrink: 1,
          minWidth: 0,
        }}
      >
        Qtracker
      </Text>}
      {returnFleetId&&!activeFleetId&&<Pressable accessibilityRole="button" onPress={()=>setActiveFleetId(returnFleetId)} style={{minHeight:48,padding:12,borderWidth:1,borderColor:palette.primary,borderRadius:12,justifyContent:'center'}}><Text style={{color:palette.primary,fontWeight:'700'}}>{locale==='da-DK'?'← Tilbage til flåde':'← Back to fleet'}</Text></Pressable>}
      <AppearanceControls palette={palette} onSelect={chooseAppearance} disabled={sessionState.layoutBusy}/>
    </View>
  );
  const IconBubble = ({
    symbol,
    tone = "primary",
  }: {
    symbol: string;
    tone?: "primary" | "attention" | "critical";
  }) => (
    <View
      style={[
        styles.iconBubble,
        {
          backgroundColor:
            tone === "critical"
              ? palette.criticalSoft
              : tone === "attention"
                ? palette.attentionSoft
                : palette.primarySoft,
        },
      ]}
    >
      <Text style={[styles.iconBubbleText, { color: palette.primary }]}>
        {symbol}
      </Text>
    </View>
  );
  const SettingRow = ({
    symbol,
    title,
    detail,
    value,
    onPress,
  }: {
    symbol: string;
    title: string;
    detail: string;
    value: string;
    onPress?: () => void;
  }) => {
    const row = (
      <>
        {symbol && <IconBubble symbol={symbol} />}
        <View style={styles.settingCopy}>
          <Text style={styles.settingTitle}>{title}</Text>
          <Text style={styles.settingDetail} numberOfLines={1}>
            {detail}
          </Text>
        </View>
        <View style={styles.settingValueGroup}>
          <Text style={styles.settingValue}>{value}</Text>
          {onPress &&
            (deliveredDesign(layout) ? (
              <DesignIcon name="chevron" color={palette.muted} size={20} />
            ) : (
              <Text style={styles.chevron}>›</Text>
            ))}
        </View>
      </>
    );
    return onPress ? (
      <Pressable
        accessibilityRole="button"
        onPress={onPress}
        style={({ pressed }) => [
          styles.settingRow,
          pressed && styles.settingPressed,
        ]}
      >
        {row}
      </Pressable>
    ) : (
      <View style={styles.settingRow}>{row}</View>
    );
  };

  const SettingsSection = ({
    title,
    children,
  }: {
    title: string;
    children: React.ReactNode;
  }) => (
    <View style={styles.settingsSection}>
      <Text style={styles.sectionLabel}>{title}</Text>
      <View style={styles.settingsCard}>{children}</View>
    </View>
  );

  const FeatureRow = ({ feature }: { feature: FeatureDefinition }) => (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={t(feature.titleKey)}
      accessibilityHint={`${t(feature.summaryKey)}. ${featureSupport(feature.id, locale).label}`}
      onPress={() => openFeature(feature.id)}
      style={({ pressed }) => [
        styles.featureRow,
        pressed && styles.settingPressed,
      ]}
    >
      {deliveredDesign(layout) ? (
        <View
          style={{
            width: layout === "accessible" ? 42 : 32,
            alignItems: "center",
          }}
        >
          <DesignIcon
            name={featureIcons[feature.id] ?? "device"}
            size={layout === "accessible" ? 28 : 22}
            color={palette.primary}
          />
        </View>
      ) : (
        <IconBubble
          symbol={feature.symbol}
          tone={
            sessionState.snapshot?.hardware &&
            hardwareFeatureIds.includes(feature.id)
              ? "primary"
              : feature.status === "locked"
                ? "critical"
                : feature.status === "gated"
                  ? "attention"
                  : "primary"
          }
        />
      )}
      <View style={styles.featureCopy}>
        <Text style={styles.featureTitle}>{t(feature.titleKey)}</Text>
        <Text style={styles.featureSummary} numberOfLines={2}>
          {t(feature.summaryKey)}
        </Text>
        <FeatureSupportNote
          id={feature.id}
          locale={locale}
          color={palette.muted}
          compact
        />
      </View>
      <View style={styles.featureMeta}>
        <View
          style={[
            styles.featureStatusDot,
            {
              backgroundColor:
                featureSupport(feature.id).status === "verified"
                  ? palette.ok
                  : palette.attention,
            },
          ]}
        />
        {deliveredDesign(layout) ? (
          <DesignIcon name="chevron" color={palette.muted} size={20} />
        ) : (
          <Text style={styles.chevron}>›</Text>
        )}
      </View>
    </Pressable>
  );

  const FeatureList = ({
    section,
    include,
    exclude,
  }: {
    section: FeatureDefinition["section"];
    include?: string[];
    exclude?: string[];
  }) => {
    const sectionFeatures = (
      featuresForSection(section) as FeatureDefinition[]
    ).filter(
      (feature) =>
        (!include || include.includes(feature.id)) &&
        !exclude?.includes(feature.id),
    );
    return (
      <View style={styles.featureBlock}>
        <Text style={styles.sectionLabel}>
          {t(`feature.section.${section}`)}
        </Text>
        <View style={styles.featureCard}>
          {sectionFeatures.map((feature) => (
            <FeatureRow key={feature.id} feature={feature} />
          ))}
          {section === "account" && sessionState.signedIn && (
            <>
              <SettingRow
                symbol=""
                title={t("product.language")}
                detail={locale === "da-DK" ? "Dansk" : "English"}
                value=""
                onPress={() =>
                  chooseAppearance({language:locale === "da-DK" ? "en-GB" : "da-DK"})
                }
              />
              <SettingRow
                symbol=""
                title={t("product.appearance")}
                detail={t(
                  themeMode === "dark" ? "appearance.dark" : "appearance.light",
                )}
                value=""
                onPress={() =>
                  chooseAppearance({theme:themeMode === "dark" ? "light" : "dark"})
                }
              />
              <SettingRow
                symbol=""
                title={t("local.logout")}
                detail={sessionState.accountEmail ?? ""}
                value=""
                onPress={() => void session.logout()}
              />
            </>
          )}
        </View>
      </View>
    );
  };

  const connectedSupplemental: Record<ViewKey, React.ReactNode> = {
    overview: null,
    map: (
      <FeatureList
        section="location"
        exclude={["uploadInterval", "routeHistory"]}
      />
    ),
    care: (
      <>
        <FeatureList
          section="care"
          exclude={["activity", "heartPressure", "temperature", "oxygen"]}
        />
        <FeatureList section="communication" />
      </>
    ),
    alarms: null,
    activity: null,
    events: <FeatureList section="device" include={["notifications"]} />,
    device: (
      <>
        <FeatureList section="safety" exclude={["findTracker"]} />
        <FeatureList
          section="device"
          exclude={["deviceInfo", "notifications"]}
        />
        <FeatureList section="account" exclude={["userLogin", "logout"]} />
      </>
    ),
  };

  const frame = (children: React.ReactNode) => (
    <PhoneNotificationsProvider
      session={session}
      locale={locale}
      palette={palette}
    >
      {children}
    </PhoneNotificationsProvider>
  );
  const FeatureModal = () =>
    !activeFeature ? null : (
      <Modal
        visible
        transparent
        animationType="slide"
        onRequestClose={() => setActiveFeatureId(null)}
      >
        <View style={styles.modalRoot}>
          <SafeAreaView style={styles.featureSheet}>
            <ScrollView contentContainerStyle={styles.sheetContent}>
              <Text style={styles.sheetTitle}>{t(activeFeature.titleKey)}</Text>
              <Text style={styles.sheetSubtitle}>
                {t(activeFeature.summaryKey)}
              </Text>
              <FeatureSupportNote
                key={activeFeature.id}
                id={activeFeature.id}
                locale={locale}
                color={palette.muted}
              />
              {activeFeature.id === "faq" ? (
                <HelpPanel locale={locale} palette={palette} />
              ) : activeFeature.id === "deviceInfo" ? (
                <Text style={styles.noteText}>
                  {t("feature.deviceInfoBody")}
                </Text>
              ) : null}
              <Pressable
                style={styles.secondaryButton}
                onPress={() => setActiveFeatureId(null)}
              >
                <Text style={styles.secondaryButtonText}>
                  {t("common.close")}
                </Text>
              </Pressable>
            </ScrollView>
          </SafeAreaView>
        </View>
      </Modal>
    );
  if (booting || sessionState.restoring)
    return frame(
      <SafeAreaView style={styles.safeArea}>
        <View
          style={{
            flex: 1,
            alignItems: "center",
            justifyContent: "center",
            gap: 16,
          }}
        >
          <Text style={styles.personName}>Qtracker</Text>
          <ActivityIndicator color={palette.primary} />
          <Text style={styles.settingDetail}>
            {locale === "da-DK"
              ? "Åbner din tracker…"
              : "Opening your tracker…"}
          </Text>
        </View>
      </SafeAreaView>,
    );
  if (!sessionState.signedIn)
    return frame(
      <SafeAreaView style={styles.safeArea}>
        <StatusBar style={themeMode === "dark" ? "light" : "dark"} />
        {toolbar}
        <LocalApiScreen
          invitationPending={!!invitationLink.code}
          locale={locale}
          palette={palette}
          state={{ ...sessionState, address: SERVICE_URL, mode: TEST_MODE }}
          session={session}
          onClose={leaveServer}
          onLanguage={onLanguage}
          onMapTest={() => setMapDiagnostic(true)}
        />
      </SafeAreaView>,
    );
  if (mapDiagnostic)
    return frame(
      <SafeAreaView style={styles.safeArea}>
        <Pressable
          style={styles.testModeButton}
          onPress={() => setMapDiagnostic(false)}
        >
          <Text style={styles.settingValue}>{t("common.close")}</Text>
        </Pressable>
        <MapDiagnostic locale={locale} color={palette.muted} />
      </SafeAreaView>,
    );
  if(activeFleetId&&sessionState.signedIn)return frame(<PaletteContext.Provider value={palette}><CopyContext.Provider value={locale}><CustomerFleet key={activeFleetId} id={activeFleetId} initialView={fleetViews.current.get(activeFleetId)} onViewState={view=>fleetViews.current.set(activeFleetId,view)} session={session} palette={palette} locale={locale} toolbar={toolbar} onClose={()=>{setActiveFleetId(null);setReturnFleetId(null);}} onDevice={id=>{void session.selectDevice(id).then(ok=>{if(ok){setReturnFleetId(activeFleetId);setActiveFleetId(null);setView('overview');}});}}/></CopyContext.Provider></PaletteContext.Provider>);
  if (deliveredDesign(layout))
    return frame(
      !browserNavigationReady?<View style={{padding:24}}><Text>{locale==='da-DK'?'Gendanner visning…':'Restoring view…'}</Text></View>:<DeliveredApplication
        key={`${sessionState.accountEmail}:${session.selectedTrackerId}`}
        state={sessionState}
        session={session}
        locale={locale}
        palette={palette}
        view={view}
        onView={setView}
        onLanguage={onLanguage}
        onTheme={onTheme}
        onLogout={leaveServer}
        toolbar={toolbar}
        onFleet={Platform.OS==='web'?openFleet:undefined}
      />,
    );
  return frame(
    <SafeAreaView style={styles.safeArea}>
      <StatusBar style={themeMode === "dark" ? "light" : "dark"} />
      <View style={styles.app}>
        {toolbar}
        <View style={styles.header}>
          <View style={styles.avatar}>
            <Text style={styles.avatarText}>Q</Text>
          </View>
          <View style={styles.identity}>
            <Text style={styles.greeting}>{t("local.tracker")}</Text>
            <Text style={styles.personName}>
              {sessionState.snapshot?.device?.displayName ??
                session.selectedTrackerId ??
                "Qtracker"}
            </Text>
          </View>
          {view === "overview" && (
            <Pressable
              style={styles.testModeButton}
              onPress={() => setCustomizing(true)}
            >
              <Text style={styles.settingValue}>{t("product.customize")}</Text>
            </Pressable>
          )}
        </View>
        <ScrollView
          key={view}
          style={styles.scroll}
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
        >
          <ConnectedTrackerScreen
            customizing={customizing}
            onCustomizeDone={() => setCustomizing(false)}
            onFeature={openFeature}
            view={view}
            locale={locale}
            palette={palette}
            state={sessionState}
            session={session}
            onView={setView}
            onLanguage={onLanguage}
            onTheme={onTheme}
            activityProfile={{
              strideCm:
                sessionState.snapshot?.device?.activityProfile?.strideCm ?? NaN,
              weightKg:
                sessionState.snapshot?.device?.activityProfile?.weightKg ?? NaN,
            }}
            onActivitySettings={() => openFeature("activity")}
            activityRouteDate={activityRouteDate}
            onRouteDay={(date) => {
              setActivityRouteDate(date);
              setView("map");
            }}
            supplemental={connectedSupplemental[view]}
          />
        </ScrollView>
        <View style={styles.bottomNav} accessibilityRole="tablist">
          {[navItems].map((row, i) => (
            <View
              key={i}
              style={{ flexDirection: "row", width: "100%", flexShrink: 0 }}
            >
              {row.map((item) => (
                <Pressable
                  key={item.key}
                  accessibilityRole="tab"
                  accessibilityState={{ selected: view === item.key }}
                  onPress={() => setView(item.key)}
                  style={[
                    styles.navItem,
                    view === item.key && {
                      backgroundColor: palette.primarySoft,
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.navSymbol,
                      view === item.key && styles.navSelected,
                    ]}
                  >
                    {item.symbol}
                  </Text>
                  <Text
                    style={[
                      styles.navLabel,
                      view === item.key && styles.navSelected,
                    ]}
                    numberOfLines={1}
                  >
                    {t(
                      item.key === "map"
                        ? "server.positionTab"
                        : `navigation.${item.key}`,
                    )}
                  </Text>
                  {item.key === "events" &&
                    pendingAlarms(sessionState.snapshot).length > 0 && (
                      <View style={styles.alarmBadge}>
                        <Text style={styles.alarmBadgeText}>
                          {pendingAlarms(sessionState.snapshot).length}
                        </Text>
                      </View>
                    )}
                </Pressable>
              ))}
            </View>
          ))}
        </View>
        {activeFeatureId === "notifications" && sessionState.signedIn ? (
          <Modal
            visible
            animationType="slide"
            onRequestClose={() => setActiveFeatureId(null)}
          >
            <SafeAreaView style={{ flex: 1, backgroundColor: palette.canvas }}>
              <ScrollView contentContainerStyle={{ padding: 20, gap: 16 }}>
                <Pressable
                  accessibilityRole="button"
                  style={styles.secondaryButton}
                  onPress={() => setActiveFeatureId(null)}
                >
                  <Text style={styles.secondaryButtonText}>
                    {t("common.close")}
                  </Text>
                </Pressable>
                <PhoneNotificationPanel />
              </ScrollView>
            </SafeAreaView>
          </Modal>
        ) : activeFeatureId === "gpsFence" && sessionState.signedIn ? (
          <GpsZoneModal
            key={`${sessionState.accountEmail}:${session.selectedTrackerId}`}
            locale={locale}
            palette={palette}
            state={sessionState}
            session={session}
            onClose={() => setActiveFeatureId(null)}
          />
        ) : activeFeatureId === "locationSources" && sessionState.signedIn ? (
          <PositionSourcesModal
            locale={locale}
            palette={palette}
            onClose={() => setActiveFeatureId(null)}
          />
        ) : activeFeatureId === "trackerPassword" && Platform.OS !== "web" && sessionState.signedIn ? (
          <TrackerPasswordModal
            key={`${sessionState.accountEmail}:${session.selectedTrackerId}`}
            locale={locale}
            palette={palette}
            state={sessionState}
            session={session}
            onClose={() => setActiveFeatureId(null)}
          />
        ) : activeFeatureId === "password" && sessionState.signedIn ? (
          <AccountPasswordModal
            key={sessionState.accountEmail}
            locale={locale}
            palette={palette}
            state={sessionState}
            session={session}
            onClose={() => setActiveFeatureId(null)}
            onTracker={Platform.OS !== "web" ? () => setActiveFeatureId("trackerPassword") : undefined}
          />
        ) : activeFeatureId === "activity" && sessionState.signedIn ? (
          <ActivityProfileModal
            key={`${sessionState.accountEmail}:${session.selectedTrackerId}`}
            locale={locale}
            palette={palette}
            state={sessionState}
            session={session}
            onClose={() => setActiveFeatureId(null)}
          />
        ) : sessionState.signedIn &&
          activeFeatureId &&
          hardwareFeatureIds.includes(activeFeatureId) ? (
          <HardwareFeatureModal
            key={`${sessionState.accountEmail}:${session.selectedTrackerId}:${activeFeatureId}`}
            featureId={activeFeatureId}
            locale={locale}
            palette={palette}
            state={sessionState}
            session={session}
            onClose={() => setActiveFeatureId(null)}
          />
        ) : (
          FeatureModal()
        )}
      </View>
    </SafeAreaView>,
  );
}
function createStyles(
  palette: Palette,
  designStyles: ReturnType<typeof useDesignStyles>,
) {
  const shadow = Platform.select({
    ios: {
      shadowColor: "#17332A",
      shadowOffset: { width: 0, height: 8 },
      shadowOpacity: 0.08,
      shadowRadius: 18,
    },
    android: { elevation: 3 },
    default: {},
  });
  return designStyles({
    safeArea: { flex: 1, backgroundColor: palette.canvas, paddingTop: 0 },
    app: { flex: 1, backgroundColor: palette.canvas },
    testModeBar: {
      paddingHorizontal: 20,
      paddingVertical: 6,
      flexDirection: "row",
      alignItems: "center",
      gap: 12,
      borderBottomWidth: 1,
      borderColor: palette.line,
    },
    sourceDot: { width: 8, height: 8, borderRadius: 4 },
    testModeButton: {
      minHeight: 44,
      justifyContent: "center",
      paddingHorizontal: 8,
    },
    header: {
      minHeight: 68,
      paddingHorizontal: 20,
      paddingVertical: 8,
      flexDirection: "row",
      alignItems: "center",
      gap: 11,
    },
    avatar: {
      width: 42,
      height: 42,
      borderRadius: 15,
      backgroundColor: palette.primary,
      alignItems: "center",
      justifyContent: "center",
    },
    avatarText: { color: palette.primaryText, fontSize: 16, fontWeight: "800" },
    onlineDot: {
      position: "absolute",
      right: -2,
      bottom: -2,
      width: 12,
      height: 12,
      borderWidth: 2,
      borderColor: palette.canvas,
      borderRadius: 6,
      backgroundColor: palette.ok,
    },
    identity: { flex: 1 },
    greeting: { color: palette.muted, fontSize: 11, lineHeight: 15 },
    personName: { color: palette.ink, fontSize: 15, fontWeight: "700" },
    moreButton: {
      width: 42,
      height: 42,
      borderRadius: 14,
      borderWidth: 1,
      borderColor: palette.line,
      backgroundColor: palette.surface,
      alignItems: "center",
      justifyContent: "center",
    },
    moreText: {
      color: palette.ink,
      fontSize: 16,
      fontWeight: "800",
      letterSpacing: 1,
    },
    scroll: { minHeight: 0, flexShrink: 1, flex: 1 },
    scrollContent: { paddingHorizontal: 18, paddingTop: 6, paddingBottom: 24 },
    screen: { gap: 14 },
    hero: {
      minHeight: 184,
      padding: 22,
      borderRadius: 26,
      overflow: "hidden",
      ...shadow,
    },
    heroDecorationLarge: {
      position: "absolute",
      width: 190,
      height: 190,
      borderRadius: 95,
      borderWidth: 1,
      borderColor: "rgba(255,255,255,0.16)",
      right: -38,
      bottom: -75,
    },
    heroDecorationSmall: {
      position: "absolute",
      width: 130,
      height: 130,
      borderRadius: 65,
      borderWidth: 18,
      borderColor: "rgba(255,255,255,0.04)",
      right: -8,
      bottom: -44,
    },
    heroTop: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
    },
    heroSymbol: {
      width: 38,
      height: 38,
      borderRadius: 13,
      backgroundColor: "rgba(255,255,255,0.16)",
      alignItems: "center",
      justifyContent: "center",
    },
    heroSymbolText: {
      color: palette.primaryText,
      fontSize: 18,
      fontWeight: "800",
    },
    statusChip: {
      minHeight: 28,
      paddingHorizontal: 10,
      borderRadius: 14,
      backgroundColor: "rgba(255,255,255,0.16)",
      flexDirection: "row",
      alignItems: "center",
      gap: 6,
    },
    statusDot: {
      width: 7,
      height: 7,
      borderRadius: 4,
      backgroundColor: "#FFFFFF",
    },
    statusChipText: { color: "#FFFFFF", fontSize: 11, fontWeight: "700" },
    heroTitle: {
      maxWidth: 250,
      marginTop: 18,
      color: palette.primaryText,
      fontSize: 25,
      lineHeight: 29,
      fontWeight: "800",
      letterSpacing: -0.7,
    },
    heroBody: {
      maxWidth: 295,
      marginTop: 7,
      color: palette.primaryText,
      fontSize: 13,
      lineHeight: 19,
    },
    metrics: { flexDirection: "row", gap: 9 },
    metric: {
      flex: 1,
      minWidth: 0,
      minHeight: 102,
      padding: 13,
      borderWidth: 1,
      borderColor: palette.line,
      borderRadius: 18,
      backgroundColor: palette.surface,
      ...shadow,
    },
    metricSymbol: {
      minHeight: 20,
      color: palette.primary,
      fontSize: 17,
      fontWeight: "700",
    },
    metricValue: {
      marginTop: 10,
      color: palette.ink,
      fontSize: 18,
      fontWeight: "800",
      letterSpacing: -0.5,
    },
    metricLabel: { marginTop: 2, color: palette.muted, fontSize: 10 },
    card: {
      padding: 10,
      borderWidth: 1,
      borderColor: palette.line,
      borderRadius: 26,
      backgroundColor: palette.surface,
      ...shadow,
    },
    cardHeader: {
      minHeight: 44,
      paddingHorizontal: 8,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
    },
    cardHeaderGroup: { flexDirection: "row", alignItems: "center", gap: 9 },
    cardHeaderSymbol: {
      color: palette.primary,
      fontSize: 18,
      fontWeight: "700",
    },
    cardTitle: { color: palette.ink, fontSize: 14, fontWeight: "700" },
    cardMeta: { color: palette.muted, fontSize: 11 },
    quickGrid: { flexDirection: "row", flexWrap: "wrap", gap: 9 },
    quickCard: {
      width: "48.5%",
      minHeight: 74,
      paddingHorizontal: 14,
      paddingVertical: 13,
      borderWidth: 1,
      borderColor: palette.line,
      borderRadius: 18,
      backgroundColor: palette.surface,
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
      ...shadow,
    },
    quickSymbol: {
      width: 24,
      color: palette.primary,
      fontSize: 18,
      fontWeight: "800",
      textAlign: "center",
    },
    quickTitle: {
      flex: 1,
      color: palette.ink,
      fontSize: 12,
      lineHeight: 16,
      fontWeight: "700",
    },
    map: {
      position: "relative",
      overflow: "hidden",
      backgroundColor: palette.map,
    },
    mapSmall: { height: 114, borderRadius: 18 },
    mapLarge: { height: 390, borderRadius: 26, ...shadow },
    road: {
      position: "absolute",
      height: 22,
      width: 480,
      backgroundColor: palette.road,
    },
    roadOne: { top: 70, left: -65, transform: [{ rotate: "27deg" }] },
    roadTwo: { top: 190, left: -60, transform: [{ rotate: "-63deg" }] },
    roadThree: { top: 285, left: -80, transform: [{ rotate: "18deg" }] },
    safeZone: {
      position: "absolute",
      top: "50%",
      left: "50%",
      width: 128,
      height: 128,
      marginLeft: -64,
      marginTop: -64,
      borderRadius: 64,
      borderWidth: 1,
      borderColor: palette.primary,
      backgroundColor: palette.primarySoft,
      opacity: 0.55,
    },
    safeZoneOffset: { left: "32%" },
    marker: {
      position: "absolute",
      top: "50%",
      left: "52%",
      width: 38,
      height: 38,
      marginLeft: -19,
      marginTop: -19,
      borderWidth: 4,
      borderColor: "#FFFFFF",
      borderRadius: 14,
      backgroundColor: palette.primary,
      alignItems: "center",
      justifyContent: "center",
      transform: [{ rotate: "-45deg" }],
    },
    markerOutside: {
      top: "30%",
      left: "73%",
      backgroundColor: palette.attention,
    },
    markerText: {
      color: "#FFFFFF",
      fontSize: 12,
      fontWeight: "800",
      transform: [{ rotate: "45deg" }],
    },
    liveBadge: {
      position: "absolute",
      top: 15,
      left: 15,
      minHeight: 30,
      paddingHorizontal: 11,
      borderRadius: 15,
      backgroundColor: palette.surface,
      flexDirection: "row",
      alignItems: "center",
      gap: 7,
      ...shadow,
    },
    liveDot: {
      width: 7,
      height: 7,
      borderRadius: 4,
      backgroundColor: palette.ok,
    },
    liveText: { color: palette.ink, fontSize: 11, fontWeight: "700" },
    mapInfo: {
      position: "absolute",
      right: 12,
      bottom: 12,
      left: 12,
      minHeight: 68,
      paddingHorizontal: 14,
      borderRadius: 17,
      backgroundColor: palette.surface,
      flexDirection: "row",
      alignItems: "center",
      ...shadow,
    },
    mapInfoMain: { flex: 1, paddingRight: 8 },
    mapInfoTitle: { color: palette.ink, fontSize: 13, fontWeight: "700" },
    mapInfoBody: { marginTop: 3, color: palette.muted, fontSize: 10 },
    mapStat: {
      minWidth: 52,
      paddingLeft: 11,
      borderLeftWidth: 1,
      borderLeftColor: palette.line,
    },
    mapStatValue: { color: palette.ink, fontSize: 11, fontWeight: "800" },
    mapStatLabel: { marginTop: 2, color: palette.muted, fontSize: 9 },
    primaryButton: {
      minHeight: 54,
      borderRadius: 17,
      backgroundColor: palette.primary,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 10,
      ...shadow,
    },
    criticalButton: { backgroundColor: palette.critical },
    primaryButtonSymbol: {
      color: palette.primaryText,
      fontSize: 18,
      fontWeight: "800",
    },
    primaryButtonText: {
      color: palette.primaryText,
      fontSize: 14,
      fontWeight: "800",
    },
    pressed: { opacity: 0.78, transform: [{ scale: 0.985 }] },
    disabled: { opacity: 0.65 },
    titleRow: {
      minHeight: 48,
      flexDirection: "row",
      alignItems: "flex-end",
      justifyContent: "space-between",
    },
    screenTitle: {
      color: palette.ink,
      fontSize: 30,
      lineHeight: 35,
      fontWeight: "800",
      letterSpacing: -1,
    },
    titleMeta: { paddingBottom: 3, color: palette.muted, fontSize: 11 },
    filters: { flexDirection: "row", gap: 8 },
    filter: {
      minHeight: 40,
      paddingHorizontal: 16,
      borderWidth: 1,
      borderColor: palette.line,
      borderRadius: 20,
      backgroundColor: palette.surface,
      alignItems: "center",
      justifyContent: "center",
    },
    filterActive: {
      borderColor: palette.primary,
      backgroundColor: palette.primarySoft,
    },
    filterText: { color: palette.muted, fontSize: 12, fontWeight: "700" },
    filterTextActive: { color: palette.primary },
    eventList: { gap: 9 },
    eventCard: {
      minHeight: 92,
      padding: 15,
      borderWidth: 1,
      borderColor: palette.line,
      borderRadius: 18,
      backgroundColor: palette.surface,
      flexDirection: "row",
      alignItems: "flex-start",
      gap: 12,
      ...shadow,
    },
    iconBubble: {
      width: 40,
      height: 40,
      borderRadius: 13,
      alignItems: "center",
      justifyContent: "center",
    },
    iconBubbleText: { fontSize: 18, fontWeight: "800" },
    eventCopy: { flex: 1 },
    eventTitle: { color: palette.ink, fontSize: 13, fontWeight: "800" },
    eventBody: {
      marginTop: 4,
      color: palette.muted,
      fontSize: 11,
      lineHeight: 16,
    },
    eventTime: { color: palette.muted, fontSize: 10 },
    eventAck: {
      marginTop: 8,
      flexDirection: "row",
      alignItems: "center",
      gap: 5,
    },
    eventAckSymbol: { color: palette.ok, fontSize: 11, fontWeight: "800" },
    eventAckText: { color: palette.ok, fontSize: 10, fontWeight: "700" },
    emptyState: {
      minHeight: 100,
      borderWidth: 1,
      borderColor: palette.line,
      borderRadius: 18,
      backgroundColor: palette.surface,
      alignItems: "center",
      justifyContent: "center",
    },
    emptyText: { color: palette.muted, fontSize: 12 },
    careHero: {
      minHeight: 116,
      paddingHorizontal: 12,
      borderWidth: 1,
      borderColor: palette.line,
      borderRadius: 24,
      backgroundColor: palette.surface,
      flexDirection: "row",
      alignItems: "center",
      ...shadow,
    },
    careMetric: { flex: 1, minWidth: 0, alignItems: "center", gap: 5 },
    careMetricValue: {
      color: palette.ink,
      fontSize: 23,
      fontWeight: "800",
      letterSpacing: -0.7,
    },
    careMetricLabel: { color: palette.muted, fontSize: 9, textAlign: "center" },
    careDivider: { width: 1, height: 48, backgroundColor: palette.line },
    disclaimer: {
      padding: 13,
      borderWidth: 1,
      borderColor: palette.line,
      borderRadius: 16,
      backgroundColor: palette.surfaceSoft,
      flexDirection: "row",
      alignItems: "flex-start",
      gap: 9,
    },
    featureBlock: { gap: 8 },
    featureCard: {
      overflow: "hidden",
      borderWidth: 1,
      borderColor: palette.line,
      borderRadius: 24,
      backgroundColor: palette.surface,
      ...shadow,
    },
    featureRow: {
      minHeight: 76,
      paddingHorizontal: 14,
      paddingVertical: 11,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: palette.line,
      backgroundColor: palette.surface,
      flexDirection: "row",
      alignItems: "center",
      gap: 11,
    },
    featureCopy: { flex: 1, minWidth: 0 },
    featureTitle: { color: palette.ink, fontSize: 13, fontWeight: "700" },
    featureSummary: {
      marginTop: 3,
      color: palette.muted,
      fontSize: 10,
      lineHeight: 14,
    },
    featureMeta: { flexDirection: "row", alignItems: "center", gap: 6 },
    featureStatusDot: {
      width: 7,
      height: 7,
      borderRadius: 4,
      backgroundColor: palette.ok,
    },
    settingsSection: { gap: 8 },
    sectionLabel: {
      marginLeft: 3,
      color: palette.muted,
      fontSize: 10,
      fontWeight: "800",
      letterSpacing: 1.1,
      textTransform: "uppercase",
    },
    settingsCard: {
      overflow: "hidden",
      borderWidth: 1,
      borderColor: palette.line,
      borderRadius: 24,
      backgroundColor: palette.surface,
      ...shadow,
    },
    settingRow: {
      width: "100%",
      minHeight: 66,
      paddingHorizontal: 14,
      paddingVertical: 10,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: palette.line,
      backgroundColor: palette.surface,
      flexDirection: "row",
      alignItems: "center",
      gap: 11,
    },
    settingPressed: { backgroundColor: palette.surfaceSoft },
    settingCopy: { flex: 1, minWidth: 0 },
    settingTitle: { color: palette.ink, fontSize: 13, fontWeight: "700" },
    settingDetail: { marginTop: 3, color: palette.muted, fontSize: 11 },
    settingValueGroup: {
      maxWidth: 135,
      flexDirection: "row",
      alignItems: "center",
      gap: 4,
    },
    settingValue: {
      color: palette.muted,
      fontSize: 11,
      fontWeight: "700",
      textAlign: "right",
    },
    chevron: { color: palette.muted, fontSize: 20 },
    readOnlyNote: {
      padding: 14,
      borderWidth: 1,
      borderColor: palette.line,
      borderRadius: 16,
      backgroundColor: palette.surfaceSoft,
      flexDirection: "row",
      alignItems: "flex-start",
      gap: 10,
    },
    noteSymbol: { color: palette.primary, fontSize: 16, fontWeight: "800" },
    noteText: { flex: 1, color: palette.muted, fontSize: 11, lineHeight: 16 },
    modalRoot: { flex: 1, justifyContent: "flex-end" },
    modalBackdrop: {
      ...StyleSheet.absoluteFill,
      backgroundColor: "rgba(5,15,12,0.52)",
    },
    featureSheet: {
      width: "100%",
      maxWidth: 680,
      maxHeight: "91%",
      alignSelf: "center",
      overflow: "hidden",
      borderTopLeftRadius: 30,
      borderTopRightRadius: 30,
      backgroundColor: palette.canvas,
      ...shadow,
    },
    sheetHandle: {
      width: 42,
      height: 4,
      marginTop: 9,
      marginBottom: 4,
      alignSelf: "center",
      borderRadius: 2,
      backgroundColor: palette.line,
    },
    sheetHeader: {
      minHeight: 82,
      paddingHorizontal: 18,
      paddingVertical: 12,
      borderBottomWidth: 1,
      borderBottomColor: palette.line,
      flexDirection: "row",
      alignItems: "center",
      gap: 11,
    },
    sheetHeaderCopy: { flex: 1, minWidth: 0 },
    sheetTitle: {
      color: palette.ink,
      fontSize: 19,
      fontWeight: "800",
      letterSpacing: -0.4,
    },
    sheetSubtitle: {
      marginTop: 3,
      color: palette.muted,
      fontSize: 11,
      lineHeight: 15,
    },
    closeButton: {
      width: 38,
      height: 38,
      borderWidth: 1,
      borderColor: palette.line,
      borderRadius: 13,
      backgroundColor: palette.surface,
      alignItems: "center",
      justifyContent: "center",
    },
    closeButtonText: {
      color: palette.ink,
      fontSize: 25,
      lineHeight: 27,
      fontWeight: "500",
    },
    sheetContent: { padding: 18, paddingBottom: 30, gap: 16 },
    statusNotice: {
      padding: 14,
      borderRadius: 17,
      backgroundColor: palette.primarySoft,
      flexDirection: "row",
      alignItems: "flex-start",
      gap: 10,
    },
    statusNoticeDot: {
      width: 9,
      height: 9,
      marginTop: 4,
      borderRadius: 5,
      backgroundColor: palette.ok,
    },
    statusNoticeCopy: { flex: 1 },
    statusNoticeTitle: { color: palette.ink, fontSize: 12, fontWeight: "800" },
    statusNoticeBody: {
      marginTop: 4,
      color: palette.muted,
      fontSize: 11,
      lineHeight: 16,
    },
    infoPanel: {
      padding: 16,
      borderWidth: 1,
      borderColor: palette.line,
      borderRadius: 18,
      backgroundColor: palette.surface,
    },
    infoPanelTitle: { color: palette.ink, fontSize: 13, fontWeight: "800" },
    infoPanelBody: {
      marginTop: 6,
      color: palette.muted,
      fontSize: 11,
      lineHeight: 17,
    },
    controls: { gap: 14 },
    controlRow: {
      minHeight: 60,
      paddingHorizontal: 14,
      borderWidth: 1,
      borderColor: palette.line,
      borderRadius: 16,
      backgroundColor: palette.surface,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
    },
    controlGroup: { gap: 8 },
    controlLabel: {
      flex: 1,
      color: palette.ink,
      fontSize: 12,
      fontWeight: "700",
    },
    textField: {
      minHeight: 50,
      paddingHorizontal: 14,
      borderWidth: 1,
      borderColor: palette.line,
      borderRadius: 15,
      backgroundColor: palette.surface,
      color: palette.ink,
      fontSize: 13,
    },
    choiceWrap: { flexDirection: "row", flexWrap: "wrap", gap: 7 },
    choice: {
      minHeight: 38,
      paddingHorizontal: 13,
      borderWidth: 1,
      borderColor: palette.line,
      borderRadius: 19,
      backgroundColor: palette.surface,
      alignItems: "center",
      justifyContent: "center",
    },
    choiceSelected: {
      borderColor: palette.primary,
      backgroundColor: palette.primarySoft,
    },
    choiceText: { color: palette.muted, fontSize: 11, fontWeight: "700" },
    choiceTextSelected: { color: palette.primary },
    secondaryButton: {
      minHeight: 50,
      paddingHorizontal: 16,
      borderWidth: 1,
      borderColor: palette.primary,
      borderRadius: 16,
      backgroundColor: palette.primarySoft,
      alignItems: "center",
      justifyContent: "center",
    },
    secondaryButtonText: {
      color: palette.primary,
      fontSize: 13,
      fontWeight: "800",
    },
    bottomNav: {
      flexShrink: 0,
      minHeight: 78,
      paddingHorizontal: 10,
      paddingTop: 8,
      paddingBottom: Platform.OS === "android" ? 9 : 4,
      borderTopWidth: 1,
      borderTopColor: palette.line,
      backgroundColor: palette.surface,
      flexDirection: "row",
    },
    navItem: {
      flex: 1,
      minHeight: 58,
      borderRadius: 14,
      alignItems: "center",
      justifyContent: "center",
      gap: 3,
    },
    navIndicator: {
      position: "absolute",
      top: 0,
      width: 22,
      height: 3,
      borderRadius: 2,
      backgroundColor: palette.primary,
    },
    navSymbol: { color: palette.muted, fontSize: 21, fontWeight: "700" },
    navLabel: {
      maxWidth: "92%",
      color: palette.muted,
      fontSize: 9,
      fontWeight: "700",
    },
    navSelected: { color: palette.primary },
    alarmBadge: {
      position: "absolute",
      top: 3,
      left: "58%",
      minWidth: 17,
      height: 17,
      paddingHorizontal: 4,
      borderWidth: 2,
      borderColor: palette.surface,
      borderRadius: 9,
      backgroundColor: palette.critical,
      alignItems: "center",
      justifyContent: "center",
    },
    alarmBadgeText: { color: "#FFFFFF", fontSize: 8, fontWeight: "800" },
    toast: {
      position: "absolute",
      right: 18,
      bottom: 88,
      left: 18,
      minHeight: 46,
      paddingHorizontal: 15,
      borderRadius: 15,
      backgroundColor: palette.ink,
      alignItems: "center",
      justifyContent: "center",
      ...shadow,
    },
    toastText: { color: palette.surface, fontSize: 12, fontWeight: "700" },
  });
}
